import { workspaceDestinations, workspaceHref, workspaceTitle } from '../features/workspace/workspace-navigation';
import { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { BookOpen, CalendarDays, CalendarCheck2, Cloud, CloudOff, FileText, GraduationCap, LayoutDashboard, LoaderCircle, Menu, TriangleAlert, X } from 'lucide-react';
import { Link, Navigate, NavLink, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import type { SessionSnapshot } from '../lib/api';
import { useSync } from '../lib/sync-context';
import { selectFirstPayload } from '../lib/workspace-records';
import type { ProfileSettingsDocument } from '../types/sync';
import { TodoReminderBridge } from './TodoReminderBridge';

const AgentWorkspace = lazy(() => import('../features/agent/AgentWorkspace').then(module => ({ default: module.AgentWorkspace })));
const LearningOverview = lazy(() => import('../features/home/LearningOverview').then(module => ({ default: module.LearningOverview })));
const CalendarWorkspace = lazy(() => import('../features/home/CalendarWorkspace').then(module => ({ default: module.CalendarWorkspace })));
const NoteWorkspace = lazy(() => import('./NoteWorkspace').then(module => ({ default: module.NoteWorkspace })));
const CoursesWorkspace = lazy(() => import('../features/courses/CoursesWorkspace').then(module => ({ default: module.CoursesWorkspace })));
const PlanWorkspace = lazy(() => import('../features/plan/PlanWorkspace').then(module => ({ default: module.PlanWorkspace })));
const ProfileWorkspace = lazy(() => import('../features/profile/ProfileWorkspace').then(module => ({ default: module.ProfileWorkspace })));
const AcademicsWorkspace = lazy(() => import('../features/profile/ProfileWorkspace').then(module => ({ default: module.AcademicsWorkspace })));
const ClassroomWorkspace = lazy(() => import('../features/classroom/ClassroomWorkspace').then(module => ({ default: module.ClassroomWorkspace })));

/** 网站导航只负责入口；笔记目录和文档状态由笔记工作区持有。 */
export function AppShell({ session, onSession }: {
  session: SessionSnapshot;
  onSession: (session: SessionSnapshot) => void;
}) {
  const { snapshot } = useSync();
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuButton = useRef<HTMLButtonElement>(null);
  const preview = import.meta.env.DEV && session.user.id === 'web-ui-preview-user';
  const href = (path: string) => workspaceHref(path, preview);
  useEffect(() => {
    setMenuOpen(false);
    if (location.pathname !== '/notes') document.title = `${workspaceTitle(location.pathname)} · 致学`;
    // 显式预览在所有功能间保持隔离，刷新也不能误入真实账号流程。
    const concreteRoute = workspaceDestinations.some(item => item.path === location.pathname) || ['/profile', '/academics', '/agent'].includes(location.pathname) || location.pathname.startsWith('/classroom/');
    if (preview && concreteRoute && !new URLSearchParams(location.search).has('ui-preview')) {
      navigate(href(`${location.pathname}${location.search}${location.hash}`), { replace: true });
    }
  }, [location.pathname, location.search, preview, navigate]);
  useEffect(() => {
    if (!menuOpen) return;
    const close = (event: KeyboardEvent) => { if (event.key === 'Escape') { setMenuOpen(false); menuButton.current?.focus(); } };
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, [menuOpen]);
  const profile = useMemo(() => selectFirstPayload<ProfileSettingsDocument>(snapshot.records, 'profile.settings'), [snapshot.records]);
  const displayName = profile?.displayName || session.user.displayName || '学习者';
  return (
    <div className="web-shell">
      <a className="skip-to-content" href="#workspace-content">跳到内容</a>
      <header className="web-header">
        <NavLink className="web-brand" to={href('/home')} aria-label="致学学习空间"><GraduationCap /><strong>致学</strong><span>学习空间</span></NavLink>
        <nav className="web-navigation" aria-label="主导航">
          {workspaceDestinations.map((item, index) => {
            const Icon = [LayoutDashboard, FileText, BookOpen, CalendarDays, CalendarCheck2][index]!;
            return <NavLink key={item.path} to={href(item.path)}><Icon />{item.label}</NavLink>;
          })}
        </nav>
        <div className="web-account"><NavLink className="web-agent-link" to={href('/agent')}>Agent</NavLink>
          {import.meta.env.DEV && new URLSearchParams(window.location.search).has('ui-preview') && <small className="web-preview-label">开发预览</small>}
          <span className={`web-sync is-${snapshot.status}`} title={snapshot.status === 'offline' ? '修改保存在此设备，联网后同步' : '云同步状态'}>
            {snapshot.status === 'syncing' ? <LoaderCircle className="spin" /> : snapshot.status === 'offline' ? <CloudOff /> : snapshot.status === 'conflict' ? <TriangleAlert /> : <Cloud />}
            <span>{snapshot.status === 'syncing' ? '同步中' : snapshot.status === 'offline' ? '离线' : snapshot.status === 'conflict' ? '待处理冲突' : snapshot.lastSuccessfulSyncAt ? '已同步' : '本地工作区'}</span>
          </span>
          <NavLink to={href('/profile')} className="web-account-link" aria-label="账号与设置"><span>{displayName.slice(0, 1)}</span><span>账号与设置</span></NavLink>
          <button ref={menuButton} className="web-menu-toggle" aria-label={menuOpen ? '关闭功能导航' : '全部功能'} aria-expanded={menuOpen} aria-controls="web-feature-menu" onClick={() => setMenuOpen(value => !value)}>{menuOpen ? <X /> : <Menu />}</button>
        </div>
      </header>
      {menuOpen && <><button className="web-menu-backdrop" aria-label="关闭功能导航" onClick={() => setMenuOpen(false)} /><nav id="web-feature-menu" className="web-feature-menu" aria-label="全部功能" onClick={event => { if ((event.target as HTMLElement).closest('a')) setMenuOpen(false); }}>
        {workspaceDestinations.map(item => <NavLink key={item.path} to={href(item.path)}>{item.label}</NavLink>)}
        <hr /><Link to={href('/agent')}>Agent 学习助手</Link><Link to={href('/courses?view=records')}>课堂记录</Link><Link to={href('/courses?view=timetable')}>每周课表</Link><Link to={href('/plan?tab=goals')}>长期目标</Link><Link to={href('/academics')}>学校信息</Link><Link to={href('/profile')}>账号与设置</Link>
      </nav></>}

      <main className="web-stage" id="workspace-content" tabIndex={-1}>
        <Suspense fallback={<div className="route-loading"><LoaderCircle className="spin" /><span>正在打开…</span></div>}>
          <Routes>
            <Route path="/" element={<Navigate to={href('/home')} replace />} />
            <Route path="/notes" element={<NoteWorkspace session={session} onSession={onSession} />} />
            <Route path="/agent" element={<AgentWorkspace key={session.user.id} />} />
            <Route path="/home" element={<LearningOverview />} />
            <Route path="/calendar" element={<CalendarWorkspace />} />
            <Route path="/courses" element={<CoursesWorkspace />} />
            <Route path="/academics" element={<AcademicsWorkspace />} />
            <Route path="/plan" element={<PlanWorkspace />} />
            <Route path="/profile" element={<ProfileWorkspace session={session} onSession={onSession} />} />
            <Route path="/classroom/:sessionID" element={<ClassroomWorkspace />} />
            <Route path="*" element={<Navigate to={href('/home')} replace />} />
          </Routes>
        </Suspense>
      </main>
      <TodoReminderBridge />
    </div>
  );
}
